import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { 
  Calendar, Wallet, TrendingUp, Building2, Home, Shield, Lock, X,
  LayoutDashboard, Users, Layers, MessageCircle, Check, CheckCircle, Banknote,
  FileText, Edit, Crown
} from "lucide-react";
import { API } from "@/App";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { formatDateToLocal } from "@/lib/utils";
import { parseTurkishNumber } from "@/utils/numberUtils";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

export default function TrainerEditModal({
  isOpen,
  onClose,
  trainer,
  businessId,
  businessBranches = [],
  businessAcceptedCards = [],
  businessTrainingTypes = [],
  onSuccess,
}) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "events", "packages", "employees", "admin", "surveys", "reports", "evaluations"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [editingTrainer, setEditingTrainer] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [previousPermissions, setPreviousPermissions] = useState(null);
  const [newSalaryAmount, setNewSalaryAmount] = useState("");
  const [salaryEffectiveDate, setSalaryEffectiveDate] = useState("");
  const [businessCardPrices, setBusinessCardPrices] = useState({});
  const [availableBusinesses, setAvailableBusinesses] = useState([]);

  useEffect(() => {
    if (trainer && isOpen) {
      setPreviousPermissions(null);
      setEditingTrainer({
        ...trainer,
        name: trainer.name || "",
        surname: trainer.surname || "",
        branches: trainer.branches || [],
        known_branches: trainer.known_branches || [],
        commission_type: trainer.commission_type || "percentage",
        commission_rates: trainer.commission_rates || { one_on_one: "0", duet: "0", group: "0" },
        commission_settings: trainer.commission_settings || {
          calculation_method: "per_lesson",
          card_commissions: {},
          package_commissions: { type: "percentage", rates: {} }
        },
        work_type: trainer.work_type || "commission_receiving",
        own_business_id: trainer.own_business_id || "",
        own_business_name: trainer.own_business_name || "",
        giving_commission_type: trainer.giving_commission_type || "percentage",
        giving_commission_rate: trainer.giving_commission_rate || "",
        rent_amount: trainer.rent_amount || "",
        rent_start_date: trainer.rent_start_date || "",
        is_manager: trainer.is_manager || false,
        is_co_owner: trainer.is_co_owner || false,
        permissions: trainer.permissions || {},
        can_approve_reservations: trainer.can_approve_reservations || false,
      });
      setNewSalaryAmount("");
      setSalaryEffectiveDate("");
      fetchBusinessCardPrices();
      fetchAvailableBusinesses();
    }
  }, [trainer, isOpen]);

  const fetchBusinessCardPrices = async () => {
    try {
      const res = await fetch(`${API}/businesses/${businessId}/card-prices`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        const cardPricesArray = data.card_prices || [];
        const cardPricesObj = {};
        if (Array.isArray(cardPricesArray)) {
          cardPricesArray.forEach(cp => {
            if (cp.card_name) {
              cardPricesObj[cp.card_name] = cp.single_session_price || cp.price || 0;
            }
          });
        }
        setBusinessCardPrices(cardPricesObj);
      }
    } catch (error) {
      console.error("Error fetching card prices:", error);
    }
  };

  const fetchAvailableBusinesses = async () => {
    try {
      const res = await fetch(`${API}/businesses`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAvailableBusinesses(data);
      }
    } catch (error) {
      console.error("Error fetching businesses:", error);
    }
  };

  // Filter available businesses for editing trainer
  const editingTrainerBusinesses = useMemo(() => {
    if (!editingTrainer) return [];
    const trainerUserId = editingTrainer.user_id;
    if (!trainerUserId) return [];
    const ownedBusinesses = availableBusinesses.filter(b => 
      b.owner_id === trainerUserId || editingTrainer.own_business_id === b.business_id
    );
    return ownedBusinesses.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [editingTrainer, availableBusinesses]);

  const handleSave = async () => {
    if (!editingTrainer) return;
    if (!editingTrainer.branches || editingTrainer.branches.length === 0) {
      toast.error(t('common:selectAtLeastOneBranch'));
      return;
    }
    if (isSaving) return;
    setIsSaving(true);

    try {
      // Clean commission rates
      const cleanCommissionRates = {};
      if (editingTrainer.commission_rates) {
        Object.entries(editingTrainer.commission_rates).forEach(([key, value]) => {
          cleanCommissionRates[key] = value === "" || value === null || value === undefined ? 0 : parseFloat(value) || 0;
        });
      }

      // Clean commission settings
      const rawSettings = editingTrainer.commission_settings || {};
      const cleanCommissionSettings = {
        calculation_method: rawSettings.calculation_method || "per_lesson",
        commission_scope: rawSettings.commission_scope || "own_lessons",
        card_commissions: {},
        package_commissions: {
          type: rawSettings.package_commissions?.type || "percentage",
          rates: {}
        }
      };

      // Clean card commissions
      if (rawSettings.card_commissions) {
        Object.entries(rawSettings.card_commissions).forEach(([cardName, cardConfig]) => {
          const cleanRates = {};
          if (cardConfig.rates) {
            Object.entries(cardConfig.rates).forEach(([trainingType, rate]) => {
              cleanRates[trainingType] = rate === "" || rate === null || rate === undefined ? 0 : parseFloat(rate) || 0;
            });
          }
          cleanCommissionSettings.card_commissions[cardName] = {
            type: cardConfig.type || "percentage",
            rates: cleanRates
          };
        });
      }

      // Clean package commissions
      if (rawSettings.package_commissions?.rates) {
        Object.entries(rawSettings.package_commissions.rates).forEach(([trainingType, rate]) => {
          cleanCommissionSettings.package_commissions.rates[trainingType] = 
            rate === "" || rate === null || rate === undefined ? 0 : parseFloat(rate) || 0;
        });
      }

      const updateData = {
        name: editingTrainer.name,
        surname: editingTrainer.surname,
        branches: editingTrainer.branches,
        commission_type: editingTrainer.commission_type,
        commission_rates: cleanCommissionRates,
        work_type: editingTrainer.work_type || "commission_receiving",
        own_business_id: ['commission_giving', 'rent_giving'].includes(editingTrainer.work_type) 
          ? editingTrainer.own_business_id : null,
        start_date: editingTrainer.start_date,
        giving_commission_type: editingTrainer.giving_commission_type,
        giving_commission_rate: parseFloat(editingTrainer.giving_commission_rate) || 0,
        is_manager: editingTrainer.is_manager || false,
        is_co_owner: editingTrainer.is_co_owner || false,
        permissions: editingTrainer.permissions || {},
        can_approve_reservations: editingTrainer.can_approve_reservations || false,
      };

      const commissionReceivingTypes = ['commission_receiving', 'salary_and_commission_receiving', 'commission_giving', 'rent_giving'];
      if (commissionReceivingTypes.includes(editingTrainer.work_type)) {
        updateData.commission_settings = cleanCommissionSettings;
      }

      if (editingTrainer.work_type === 'rent_giving' && editingTrainer.rent_amount) {
        updateData.rent_amount = parseFloat(editingTrainer.rent_amount) || 0;
        updateData.rent_start_date = editingTrainer.rent_start_date;
      }

      if (newSalaryAmount && salaryEffectiveDate) {
        updateData.new_salary = parseTurkishNumber(newSalaryAmount);
        updateData.salary_effective_date = salaryEffectiveDate;
      }

      const response = await fetch(`${API}/trainers/${trainer.trainer_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        toast.success(t('trainers:trainerUpdated'));
        onClose();
        onSuccess?.();
      } else {
        const error = await response.json();
        let errorMessage = t('settings:updateFailed');
        if (error.detail) {
          if (typeof error.detail === "string") {
            errorMessage = error.detail;
          } else if (Array.isArray(error.detail)) {
            errorMessage = error.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
          }
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!editingTrainer) return null;

  // Convert branches for display
  const normalizedBranches = businessBranches.map(b => {
    if (typeof b === 'string') return { id: b, name: b };
    return b;
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('trainers:editTrainer')}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="info" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="info">{t("common:info")}</TabsTrigger>
            <TabsTrigger value="commission">{t("reports:commissionsTab")}</TabsTrigger>
            <TabsTrigger value="permissions">{t("common:i̇zinler")}</TabsTrigger>
          </TabsList>

          {/* Bilgiler Tab */}
          <TabsContent value="info" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('common:firstName')}</Label>
                <Input value={editingTrainer.name || ""} onChange={(e) => setEditingTrainer({...editingTrainer, name: e.target.value})} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label>{t("common:surnameLabel")}</Label>
                <Input value={editingTrainer.surname || ""} onChange={(e) => setEditingTrainer({...editingTrainer, surname: e.target.value})} className="rounded-xl mt-1" />
              </div>
            </div>

            <div>
              <Label>{t('common:branches')}</Label>
              <p className="text-xs text-grey mb-2">{t("common:antrenorunBuIsletmedeCalistigiBranslar")}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {(editingTrainer.branches || []).map((branchName) => (
                  <span
                    key={branchName}
                    className="px-3 py-1.5 rounded-full text-sm bg-navy text-white flex items-center gap-1"
                  >
                    {getLocalizedBranch(branchName)}
                    <button
                      type="button"
                      onClick={() => {
                        const updated = (editingTrainer.branches || []).filter(b => b !== branchName);
                        setEditingTrainer({...editingTrainer, branches: updated});
                      }}
                      className="ml-1 hover:text-red-200"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {(editingTrainer.branches || []).length === 0 && (
                  <p className="text-sm text-grey">{t("common:bransAtanmamis")}</p>
                )}
              </div>
              {/* Add branch from business branches */}
              {(() => {
                const currentBranches = editingTrainer.branches || [];
                const trainerKnownBranches = editingTrainer.known_branches || [];
                const businessBranchNames = (businessBranches || []).map(b => b.name || b);
                const intersection = trainerKnownBranches.filter(b => businessBranchNames.includes(b));
                const availableBranches = intersection.filter(b => !currentBranches.includes(b));
                if (availableBranches.length === 0) return null;
                return (
                  <div className="mt-2">
                    <Select onValueChange={(val) => {
                      const updated = [...(editingTrainer.branches || []), val];
                      setEditingTrainer({...editingTrainer, branches: updated});
                    }}>
                      <SelectTrigger className="w-48 rounded-xl h-8 text-sm">
                        <SelectValue placeholder={t("trainers:addBranch")} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableBranches.map(name => (
                          <SelectItem key={name} value={name}>
                            {getLocalizedBranch(name)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })()}
            </div>

            {/* İşe Başlama Tarihi */}
            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
              <h4 className="font-semibold text-green-700 flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4" /> {t("common:workStartDate")}
              </h4>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-grey">{t("common:current")}</span>
                <span className="font-medium text-green-700">
                  {editingTrainer.start_date 
                    ? format(new Date(editingTrainer.start_date), "dd/MM/yyyy", { locale: getDateLocale() })
                    : t("common:notSpecified")}
                </span>
              </div>
              <div>
                <Label className="text-sm">{t("common:enterNewDate")}</Label>
                <DatePickerTR
                  value={editingTrainer.start_date ? new Date(editingTrainer.start_date) : null}
                  onChange={(date) => setEditingTrainer({...editingTrainer, start_date: date ? formatDateToLocal(date) : ""})}
                  placeholder={t('common:selectDate')}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>{t("common:workingType")}</Label>
              <Select
                value={editingTrainer.work_type || "commission_receiving"}
                onValueChange={(value) => {
                  const oldType = editingTrainer.work_type;
                  const updates = { work_type: value };
                  
                  if (oldType === 'commission_receiving' || oldType === 'salary_and_commission_receiving') {
                    if (value !== 'commission_receiving' && value !== 'salary_and_commission_receiving') {
                      updates.commission_settings = {};
                      updates.commission_type = null;
                      updates.commission_rates = {};
                    }
                  }
                  
                  if (oldType === 'salary_receiving' || oldType === 'salary_and_commission_receiving') {
                    if (value !== 'salary_receiving' && value !== 'salary_and_commission_receiving') {
                      updates.current_salary = 0;
                      updates.salary_start_date = null;
                    }
                  }
                  
                  if (oldType === 'commission_giving') {
                    updates.giving_commission_type = null;
                    updates.giving_commission_rate = 0;
                  }
                  
                  if (oldType === 'rent_giving') {
                    updates.rent_amount = 0;
                    updates.rent_start_date = null;
                  }
                  
                  if ((oldType === 'commission_giving' || oldType === 'rent_giving') &&
                      (value === 'commission_receiving' || value === 'salary_receiving' || value === 'salary_and_commission_receiving')) {
                    updates.own_business_id = null;
                    updates.own_business_name = null;
                  }
                  
                  setEditingTrainer({...editingTrainer, ...updates});
                }}
              >
                <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="commission_receiving" className="text-green-700">
                    <div className="flex items-center gap-2">
                      <span className="text-green-600">💰</span> {t("reports:commissionReceiving")}
                    </div>
                  </SelectItem>
                  <SelectItem value="salary_receiving" className="text-green-700">
                    <div className="flex items-center gap-2">
                      <span className="text-green-600">💵</span> {t("employees:salaryReceiving")}
                    </div>
                  </SelectItem>
                  <SelectItem value="salary_and_commission_receiving" className="text-green-700">
                    <div className="flex items-center gap-2">
                      <span className="text-green-600">💎</span> {t("reports:salaryAndCommission")}
                    </div>
                  </SelectItem>
                  <div className="my-1 border-t border-dashed border-slate-300">
                    <p className="text-xs text-grey px-2 py-1 bg-slate-50">{t("common:forTrainersWithOwnBusiness")}</p>
                  </div>
                  <SelectItem value="commission_giving" className="text-orange-700">
                    <div className="flex items-center gap-2">
                      <span className="text-orange-600">📤</span> {t("reports:commissionGiving")}
                    </div>
                  </SelectItem>
                  <SelectItem value="rent_giving" className="text-orange-700">
                    <div className="flex items-center gap-2">
                      <span className="text-orange-600">🏠</span> {t("common:rentGiving")}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              
              {/* Work Type Explanation */}
              <div className={`p-3 rounded-xl text-sm mt-2 ${
                ['commission_receiving', 'salary_receiving', 'salary_and_commission_receiving'].includes(editingTrainer.work_type)
                  ? 'bg-green-50 text-green-800'
                  : 'bg-orange-50 text-orange-800'
              }`}>
                {editingTrainer.work_type === 'commission_receiving' && (
                  <p>💰 {t("reports:perLessonCommDesc")}</p>
                )}
                {editingTrainer.work_type === 'salary_receiving' && (
                  <p>💵 {t("reports:salaryOnlyDesc")}</p>
                )}
                {editingTrainer.work_type === 'salary_and_commission_receiving' && (
                  <p>💎 {t("reports:salaryPlusCommDesc")}</p>
                )}
                {editingTrainer.work_type === 'commission_giving' && (
                  <p>📤 {t("reports:revenueShareDesc")}</p>
                )}
                {editingTrainer.work_type === 'rent_giving' && (
                  <p>🏠 {t("reports:rentDesc")}</p>
                )}
              </div>
            </div>

            {/* Maaş Ayarları */}
            {(editingTrainer.work_type === 'salary_receiving' || editingTrainer.work_type === 'salary_and_commission_receiving') && (
              <div className="p-4 bg-green-50 rounded-xl space-y-3 border border-green-200">
                <h4 className="font-semibold text-green-700 flex items-center gap-2">
                  <Wallet className="w-4 h-4" /> {t("employees:salarySettings")}
                </h4>
                <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                  <span className="text-sm text-grey">{t("employees:currentSalary")}</span>
                  <span className="font-bold text-green-600">{currencySymbol}{(editingTrainer.current_salary || 0).toLocaleString('tr-TR')}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">{t("employees:newSalary")}</Label>
                    <Input type="number" value={newSalaryAmount} onChange={(e) => setNewSalaryAmount(e.target.value)} placeholder={t("employees:ph_yeniMaas")} className="rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm">{t("common:validityDate")}</Label>
                    <DatePickerTR value={salaryEffectiveDate ? new Date(salaryEffectiveDate) : null} onChange={(date) => setSalaryEffectiveDate(date ? formatDateToLocal(date) : "")} placeholder={t('common:selectDate')} className="mt-1" />
                  </div>
                </div>
              </div>
            )}

            {/* Kendi İşletmesi */}
            {(editingTrainer.work_type === 'commission_giving' || editingTrainer.work_type === 'rent_giving') && (
              <div className="p-4 bg-orange-50 rounded-xl space-y-3 border border-orange-200">
                <h4 className="font-semibold text-orange-700 flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> {t("common:ownBusiness")}
                </h4>
                {editingTrainer.own_business_id ? (
                  <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                    <span className="text-sm text-orange-700">{t("common:currentBusiness")}</span>
                    <span className="font-bold text-orange-600">{editingTrainer.own_business_name || editingTrainerBusinesses.find(b => b.business_id === editingTrainer.own_business_id)?.name || editingTrainer.own_business_id}</span>
                  </div>
                ) : (
                  <p className="text-sm text-red-500">{t("common:businessNotSelectedCannotAcceptExternal")}</p>
                )}
                <Select 
                  value={editingTrainer.own_business_id || ""} 
                  onValueChange={(v) => {
                    const selectedBiz = editingTrainerBusinesses.find(b => b.business_id === v);
                    setEditingTrainer({
                      ...editingTrainer, 
                      own_business_id: v,
                      own_business_name: selectedBiz?.name || ""
                    });
                  }}
                >
                  <SelectTrigger className="rounded-xl border-orange-300">
                    <SelectValue placeholder={t("common:ph_i̇sletmeSecinVeyaDegistirin")} />
                  </SelectTrigger>
                  <SelectContent>
                    {editingTrainerBusinesses.length > 0 ? (
                      editingTrainerBusinesses.map(b => (
                        <SelectItem key={b.business_id} value={b.business_id}>
                          {b.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-business" disabled>{t("common:buAntrenorunKayitliIsletmesiYok")}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Prim Verme Ayarları */}
            {editingTrainer.work_type === 'commission_giving' && (
              <div className="p-4 bg-orange-50 rounded-xl space-y-3 border border-orange-200">
                <h4 className="font-semibold text-orange-700 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" /> {t("common:shareToGiveBusiness")}
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Tip</Label>
                    <Select value={editingTrainer.giving_commission_type || "percentage"} onValueChange={(v) => setEditingTrainer({...editingTrainer, giving_commission_type: v})}>
                      <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">{t("common:percentage")}</SelectItem>
                        <SelectItem value="fixed">{t("common:fixedAmount")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">{editingTrainer.giving_commission_type === 'percentage' ? 'Oran (%)' : 'Tutar ({currencySymbol})'}</Label>
                    <Input 
                      type="number" 
                      min="0" 
                      max={editingTrainer.giving_commission_type === 'percentage' ? "100" : undefined} 
                      value={editingTrainer.giving_commission_rate || ""} 
                      onChange={(e) => setEditingTrainer({...editingTrainer, giving_commission_rate: e.target.value})} 
                      placeholder={editingTrainer.giving_commission_type === 'percentage' ? "20" : "100"} 
                      className="rounded-xl mt-1" 
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Kira Ayarları */}
            {editingTrainer.work_type === 'rent_giving' && (
              <div className="p-4 bg-amber-50 rounded-xl space-y-3 border border-amber-200">
                <h4 className="font-semibold text-amber-700 flex items-center gap-2">
                  <Home className="w-4 h-4" /> {t("common:rentSettings")}
                </h4>
                {editingTrainer.rent_amount > 0 && (
                  <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                    <span className="text-sm text-amber-700">{t("common:currentRent")}</span>
                    <span className="font-bold text-amber-600">{currencySymbol}{(editingTrainer.rent_amount || 0).toLocaleString('tr-TR')}/ay</span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">{t("common:monthlyRent")}</Label>
                    <Input 
                      type="number" 
                      min="0" 
                      value={editingTrainer.rent_amount || ""} 
                      onChange={(e) => setEditingTrainer({...editingTrainer, rent_amount: e.target.value})} 
                      placeholder="5000" 
                      className="rounded-xl mt-1" 
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('common:startDate')}</Label>
                    <DatePickerTR value={editingTrainer.rent_start_date ? new Date(editingTrainer.rent_start_date) : null} onChange={(date) => setEditingTrainer({...editingTrainer, rent_start_date: date ? formatDateToLocal(date) : ""})} placeholder={t('common:selectDate')} className="mt-1" />
                  </div>
                </div>
              </div>
            )}

            {/* Yönetici Toggle */}
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">{t('employees:manager')}</p>
                  <p className="text-xs text-amber-600">{t("common:fullMenuAccess")}</p>
                </div>
              </div>
              <Switch 
                checked={editingTrainer.is_manager || false} 
                onCheckedChange={(c) => {
                  setEditingTrainer(prev => {
                    const newPermissions = c 
                      ? prev.permissions 
                      : {
                          ...prev.permissions,
                          branches: false,
                          analytics: false,
                          subscription: false,
                          salary_payments: false,
                          bonus_payments: false,
                          business_expenses: false,
                          trainer_collections: false,
                          commissions: false,
                          reports: false,
                          rooms: false,
                          packages: false,
                          price_list: false,
                          trainers: false,
                          employees: false,
                          settings: false
                        };
                    return {
                      ...prev,
                      is_manager: c,
                      permissions: newPermissions
                    };
                  });
                }} 
              />
            </div>
          </TabsContent>

          {/* Prim Tab */}
          <TabsContent value="commission" className="space-y-4 mt-4">
            {editingTrainer.work_type === "salary_receiving" ? (
              <div className="text-center py-8 text-grey">
                <Banknote className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>{t("reports:employeeSalaryOnlyNoCommission")}</p>
              </div>
            ) : (
              <>
                {['commission_giving', 'rent_giving'].includes(editingTrainer.work_type) && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                    {t("reports:commissionSettingsNote")}
                  </p>
                )}
                
                <div className="p-3 bg-blue-50 rounded-xl space-y-2">
                  <Label className="font-medium text-blue-800">{t('trainers:commissionCalculation')}</Label>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-start gap-3 p-2 bg-white rounded-lg cursor-pointer border text-sm">
                      <input type="radio" checked={editingTrainer.commission_settings?.calculation_method !== "per_package_sale"} onChange={() => setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, calculation_method: "per_lesson"}})} className="mt-0.5" />
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-green-600" />
                        <p className="font-medium">{t('trainers:perLesson')}</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 p-2 bg-white rounded-lg cursor-pointer border text-sm">
                      <input type="radio" checked={editingTrainer.commission_settings?.calculation_method === "per_package_sale"} onChange={() => setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, calculation_method: "per_package_sale"}})} className="mt-0.5" />
                      <div className="flex items-center gap-2">
                        <Banknote className="w-4 h-4 text-blue-600" />
                        <p className="font-medium">{t("packages:byPackageSale")}</p>
                      </div>
                    </label>
                    {editingTrainer.commission_settings?.calculation_method === "per_package_sale" && (
                      <div className="ml-6 p-3 bg-blue-100 rounded-lg space-y-2">
                        <Label className="text-xs font-medium text-blue-800">{t("reports:commissionScope")}</Label>
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="radio"
                            name="editModalCommissionScope"
                            checked={editingTrainer.commission_settings?.commission_scope !== "all_lessons"}
                            onChange={() => setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, commission_scope: "own_lessons"}})}
                          />
                          <span>{t("common:fromOwnLessons")}</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="radio"
                            name="editModalCommissionScope"
                            checked={editingTrainer.commission_settings?.commission_scope === "all_lessons"}
                            onChange={() => setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, commission_scope: "all_lessons"}})}
                          />
                          <span>{t("common:fromAllLessons")}</span>
                        </label>
                        <p className="text-xs text-blue-700">
                          {editingTrainer.commission_settings?.commission_scope === "all_lessons"
                            ? t("packages:allPackageSalesComm")
                            : t("packages:ownPackageSalesComm")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Commissions */}
                {businessAcceptedCards.length > 0 && (
                  <div className="space-y-2">
                    <Label className="font-medium text-amber-800 text-sm flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                      {t("reports:cardCommissions")}
                    </Label>
                    {businessAcceptedCards.map(cardName => {
                      const cardConfig = editingTrainer.commission_settings?.card_commissions?.[cardName] || { type: "percentage", rates: {} };
                      const cardPrice = businessCardPrices[cardName] || 0;
                      return (
                        <div key={cardName} className="p-3 bg-amber-50 rounded-xl space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-amber-700 text-sm">{cardName}</span>
                            <Select value={cardConfig.type || "percentage"} onValueChange={(v) => setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, card_commissions: {...editingTrainer.commission_settings?.card_commissions, [cardName]: { ...cardConfig, type: v }}}})}>
                              <SelectTrigger className="w-28 h-8 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage">{t("common:percentage")}</SelectItem>
                                <SelectItem value="fixed">{t("common:fixedAmount")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {cardConfig.type === "fixed" && cardPrice <= 0 && (
                            <p className="text-xs text-red-500 bg-red-50 p-2 rounded">
                              {t("common:cardPriceNotSet")}
                            </p>
                          )}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {businessTrainingTypes.map(trainingType => {
                              const maxFixedValue = cardConfig.type === "fixed" ? cardPrice : undefined;
                              const currentValue = cardConfig.rates?.[trainingType] || "";
                              const isOverMax = cardConfig.type === "fixed" && cardPrice > 0 && parseFloat(currentValue) > cardPrice;
                              
                              return (
                                <div key={trainingType}>
                                  <Label className="text-xs text-amber-600">{getLocalizedTrainingType(trainingType)}</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    max={cardConfig.type === "percentage" ? 100 : maxFixedValue}
                                    step="1"
                                    value={currentValue}
                                    onChange={(e) => {
                                      let value = e.target.value === "" ? "" : parseFloat(e.target.value) || 0;
                                      if (cardConfig.type === "percentage" && value > 100) {
                                        value = 100;
                                      }
                                      if (cardConfig.type === "fixed" && cardPrice > 0 && value > cardPrice) {
                                        value = cardPrice;
                                        toast.error(t("reports:commissionExceedsCardPrice"));
                                      }
                                      setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, card_commissions: {...editingTrainer.commission_settings?.card_commissions, [cardName]: {...cardConfig, rates: {...cardConfig.rates, [trainingType]: value}}}}});
                                    }}
                                    className={`rounded-lg h-8 text-sm ${isOverMax ? 'border-red-500' : ''}`}
                                    placeholder={cardConfig.type === "fixed" ? (cardPrice > 0 ? `Max ₺${cardPrice}` : currencySymbol) : "% (0-100)"}
                                    disabled={cardConfig.type === "fixed" && cardPrice <= 0}
                                  />
                                  {isOverMax && (
                                    <p className="text-xs text-red-500 mt-0.5">Max: {currencySymbol}{cardPrice}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Package Commissions */}
                <div className="space-y-2">
                  <Label className="font-medium text-emerald-800 text-sm flex items-center gap-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    {t("packages:packageCommissions")}
                  </Label>
                  <div className="p-3 bg-emerald-50 rounded-xl space-y-2">
                    <Select value={editingTrainer.commission_settings?.package_commissions?.type || "percentage"} onValueChange={(v) => setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, package_commissions: {...editingTrainer.commission_settings?.package_commissions, type: v}}})}>
                      <SelectTrigger className="w-full h-8 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">{t("common:percentage")}</SelectItem>
                        <SelectItem value="fixed">{t("common:fixedAmount")}</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {businessTrainingTypes.map(type => (
                        <div key={type}>
                          <Label className="text-xs text-emerald-600">{type}</Label>
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            value={editingTrainer.commission_settings?.package_commissions?.rates?.[type] || ""}
                            onChange={(e) => {
                              const value = e.target.value === "" ? "" : parseFloat(e.target.value) || 0;
                              setEditingTrainer({...editingTrainer, commission_settings: {...editingTrainer.commission_settings, package_commissions: {...editingTrainer.commission_settings?.package_commissions, rates: {...editingTrainer.commission_settings?.package_commissions?.rates, [type]: value}}}});
                            }}
                            className="rounded-lg h-8 text-sm"
                            placeholder={editingTrainer.commission_settings?.package_commissions?.type === "fixed" ? currencySymbol : "%"}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* İzinler Tab */}
          <TabsContent value="permissions" className="space-y-4 mt-4">
            {/* Şirket Ortağı (Kurucu Ortak) Şalteri */}
            <div className={`p-4 rounded-xl border-2 mb-3 ${editingTrainer?.is_co_owner ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${editingTrainer?.is_co_owner ? 'bg-emerald-600' : 'bg-slate-300'}`}>
                    <Crown className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="font-semibold text-navy flex items-center gap-2">
                      Şirket Ortağı (Tam Yetki)
                    </span>
                    <p className="text-xs text-grey">
                      {editingTrainer?.is_co_owner 
                        ? "Ana işletme sahibi gibi tüm finans, kasa, prim, çalışan ve yönetim ekranlarına tam erişim sağlar." 
                        : "Şirket ortağı olarak atanmamış."}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={editingTrainer?.is_co_owner || false}
                  onCheckedChange={(c) => {
                    if (c) {
                      setPreviousPermissions({
                        permissions: { ...editingTrainer.permissions },
                        is_manager: editingTrainer.is_manager,
                        can_approve_reservations: editingTrainer.can_approve_reservations
                      });

                      const allPermissionsTrue = {
                        dashboard: true, dashboard_overview: true, branches: true, analytics: true, subscription: true,
                        members: true, members_list: true, all_packages: true, member_forms: true, qr_scan: true, measurements: true,
                        can_deactivate_members: true, reservations: true, manual_reservation_complete: true, manual_reservation_approve: true,
                        backdate_reservations: true, lesson_note_edit: true, finances: true, payments: true, salary_payments: true,
                        bonus_payments: true, business_expenses: true, trainer_collections: true, commissions: true, reports: true,
                        settings: true, rooms: true, packages: true, price_list: true, trainers: true, employees: true,
                        settings_business_info: true, settings_pricing: true, settings_accruals: true, settings_working_hours: true,
                        settings_branches: true, settings_training_types: true, settings_measurements: true, settings_expense_types: true,
                        settings_numbering: true, settings_currency: true, member_no_edit: true, package_no_edit: true,
                        communication: true, messages: true, ratings: true, surveys: true, events: true, evaluations: true, goals: true,
                        can_view_revenue: true, transfer_package_sale: true
                      };

                      setEditingTrainer(prev => ({
                        ...prev,
                        is_co_owner: true,
                        is_manager: true,
                        can_approve_reservations: true,
                        permissions: allPermissionsTrue
                      }));
                    } else {
                      if (previousPermissions) {
                        setEditingTrainer(prev => ({
                          ...prev,
                          is_co_owner: false,
                          is_manager: previousPermissions.is_manager,
                          can_approve_reservations: previousPermissions.can_approve_reservations,
                          permissions: previousPermissions.permissions
                        }));
                        setPreviousPermissions(null);
                      } else {
                        setEditingTrainer(prev => ({ ...prev, is_co_owner: false }));
                      }
                    }
                  }}
                />
              </div>
            </div>
            {/* Yönetici Durumu */}
            <div className={`p-4 rounded-xl border-2 ${editingTrainer.is_manager ? 'bg-green-50 border-green-300' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${editingTrainer.is_manager ? 'bg-green-500' : 'bg-slate-300'}`}>
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="font-semibold text-navy">{t('employees:manager')}</span>
                    <p className="text-xs text-grey">
                      {editingTrainer.is_manager 
                        ? t("employees:hasManagerPermissions") 
                        : t("employees:notManagerLimited")}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={editingTrainer.is_manager || false}
                  onCheckedChange={(c) => {
                    if (!c) {
                      setEditingTrainer({
                        ...editingTrainer, 
                        is_manager: false,
                        permissions: {
                          ...editingTrainer.permissions,
                          branches: false, analytics: false, subscription: false,
                          salary_payments: false, bonus_payments: false,
                          business_expenses: false, trainer_collections: false,
                          commissions: false, reports: false, rooms: false,
                          packages: false, price_list: false, trainers: false,
                          employees: false, settings: false,
                        }
                      });
                    } else {
                      setEditingTrainer({...editingTrainer, is_manager: true});
                    }
                  }}
                />
              </div>
            </div>

            {!editingTrainer.is_manager && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs text-amber-800 flex items-center gap-2">
                  <Lock className="w-3 h-3" />
                  <span><strong>{t("employees:managerRequiredForBadge")}</strong></span>
                </p>
              </div>
            )}

            {/* İzin Şablonları */}
            <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
              <Label className="text-sm font-medium text-navy mb-2 block">{t("employees:permissionTemplates")}</Label>
              <div className="flex flex-wrap gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="text-xs rounded-lg bg-white hover:bg-green-50 hover:border-green-300"
                  disabled={!editingTrainer.is_manager}
                  onClick={() => {
                    const allPerms = {
                      // Genel
                      dashboard: true, dashboard_overview: true, branches: true, analytics: true, subscription: true,
                      // Üye Yönetimi
                      members: true, members_list: true, all_packages: true, member_forms: true, qr_scan: true, measurements: true,
                      // Rezervasyonlar
                      reservations: true,
                      // Ödemeler
                      finances: true, payments: true, salary_payments: true, bonus_payments: true,
                      business_expenses: true, trainer_collections: true, commissions: true, reports: true,
                      // İşletme Ayarları
                      settings: true, rooms: true, packages: true, price_list: true, trainers: true, employees: true,
                      // İletişim
                      communication: true, messages: true, ratings: true, surveys: true, events: true, evaluations: true, goals: true,
                      // Özel
                      can_view_revenue: true, manual_reservation_complete: true, manual_reservation_approve: true
                    };
                    setEditingTrainer({...editingTrainer, permissions: allPerms});
                  }}
                >
                  {t("employees:fullPermissions")} {!editingTrainer.is_manager && `(${t("employees:managerRequired")})`}
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="text-xs rounded-lg bg-white hover:bg-blue-50 hover:border-blue-300"
                  onClick={() => {
                    const dersPerms = {
                      // Genel
                      dashboard: true, dashboard_overview: true, branches: false, analytics: false, subscription: false,
                      // Üye Yönetimi
                      members: true, members_list: true, all_packages: true, member_forms: false, qr_scan: true, measurements: true,
                      // Rezervasyonlar
                      reservations: true,
                      // Ödemeler
                      finances: false, payments: false, salary_payments: false, bonus_payments: false,
                      business_expenses: false, trainer_collections: false, commissions: false, reports: false,
                      // İşletme Ayarları
                      settings: false, rooms: false, packages: false, price_list: false, trainers: false, employees: false,
                      // İletişim
                      communication: true, messages: true, ratings: false, surveys: false, events: false, evaluations: false, goals: false,
                      // Özel
                      can_view_revenue: false, manual_reservation_complete: false, manual_reservation_approve: false
                    };
                    setEditingTrainer({...editingTrainer, permissions: dersPerms});
                  }}
                >
                  {t("common:lessonsOnly")}
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="text-xs rounded-lg bg-white hover:bg-red-50 hover:border-red-300"
                  onClick={() => {
                    const noPerms = {
                      // Genel
                      dashboard: false, dashboard_overview: false, branches: false, analytics: false, subscription: false,
                      // Üye Yönetimi
                      members: false, members_list: false, all_packages: false, member_forms: false, qr_scan: false, measurements: false,
                      // Rezervasyonlar
                      reservations: false,
                      // Ödemeler
                      finances: false, payments: false, salary_payments: false, bonus_payments: false,
                      business_expenses: false, trainer_collections: false, commissions: false, reports: false,
                      // İşletme Ayarları
                      settings: false, rooms: false, packages: false, price_list: false, trainers: false, employees: false,
                      // İletişim
                      communication: false, messages: false, ratings: false, surveys: false, events: false, evaluations: false, goals: false,
                      // Özel
                      can_view_revenue: false, manual_reservation_complete: false, manual_reservation_approve: false
                    };
                    setEditingTrainer({...editingTrainer, permissions: noPerms});
                  }}
                >
                  {t("common:disableAll")}
                </Button>
              </div>
            </div>
            
            {/* Genel */}
            <div className="border rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <LayoutDashboard className="w-4 h-4 text-navy" />
                <span className="font-medium text-navy">{t("common:general")}</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-sm">
                  <span className="text-green-700">{t("common:mapLabel")}</span>
                  <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">{t("common:public")}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t("common:overview")}</span>
                  <Switch checked={editingTrainer.permissions?.dashboard_overview !== false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, dashboard_overview: c}})} />
                </div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                  <span className="text-grey flex items-center gap-1">{t("common:branchesLabel")} <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span>
                  <Switch checked={editingTrainer.permissions?.branches || false} disabled={!editingTrainer.is_manager} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, branches: c}})} />
                </div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                  <span className="text-grey flex items-center gap-1">Analitik <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span>
                  <Switch checked={editingTrainer.permissions?.analytics || false} disabled={!editingTrainer.is_manager} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, analytics: c}})} />
                </div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                  <span className="text-grey flex items-center gap-1">Abonelik <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span>
                  <Switch checked={editingTrainer.permissions?.subscription || false} disabled={!editingTrainer.is_manager} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, subscription: c}})} />
                </div>
              </div>
            </div>

            {/* Üye Yönetimi */}
            <div className="border rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-navy" />
                <span className="font-medium text-navy">{t("common:memberManagement")}</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t('members:title')}</span>
                  <Switch checked={editingTrainer.permissions?.members_list !== false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, members_list: c}})} />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t('members:allPackages')}</span>
                  <Switch checked={editingTrainer.permissions?.all_packages || false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, all_packages: c}})} />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t("surveys:memberForms")}</span>
                  <Switch checked={editingTrainer.permissions?.member_forms || false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, member_forms: c}})} />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">QR Tarama</span>
                  <Switch checked={editingTrainer.permissions?.qr_scan || false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, qr_scan: c}})} />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t("evaluations:measurements")}</span>
                  <Switch checked={editingTrainer.permissions?.measurements || false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, measurements: c}})} />
                </div>
              </div>
            </div>

            {/* Rezervasyonlar */}
            <div className="border rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-navy" />
                  <span className="font-medium text-navy">{t('common:reservations')}</span>
                </div>
                <Switch checked={editingTrainer.permissions?.reservations !== false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, reservations: c}})} />
              </div>
              <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-sm">
                <span className="text-grey flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  {t("admin:directApprovalAuth")}
                  <span className="text-xs text-green-600 bg-green-100 px-1 rounded">{t("admin:skipMemberApproval")}</span>
                </span>
                <Switch checked={editingTrainer.can_approve_reservations || false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, can_approve_reservations: c})} />
              </div>
            </div>

            {/* Ödemeler */}
            <div className="border rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="w-4 h-4 text-navy" />
                <span className="font-medium text-navy">{t('common:payments')}</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t("packages:packagePayments")}</span>
                  <Switch checked={editingTrainer.permissions?.payments || false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, payments: c}})} />
                </div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                  <span className="text-grey flex items-center gap-1">{t("employees:salaryPaymentsLabel")} <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span>
                  <Switch checked={editingTrainer.permissions?.salary_payments || false} disabled={!editingTrainer.is_manager} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, salary_payments: c}})} />
                </div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                  <span className="text-grey flex items-center gap-1">{t("common:extraPaymentsLabel")} <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span>
                  <Switch checked={editingTrainer.permissions?.bonus_payments || false} disabled={!editingTrainer.is_manager} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, bonus_payments: c}})} />
                </div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                  <span className="text-grey flex items-center gap-1">{t("reports:businessExpensesPermLabel")} <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span>
                  <Switch checked={editingTrainer.permissions?.business_expenses || false} disabled={!editingTrainer.is_manager} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, business_expenses: c}})} />
                </div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                  <span className="text-grey flex items-center gap-1">Gelen Kira/Prim <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span>
                  <Switch checked={editingTrainer.permissions?.trainer_collections || false} disabled={!editingTrainer.is_manager} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, trainer_collections: c}})} />
                </div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                  <span className="text-grey flex items-center gap-1">Primler <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span>
                  <Switch checked={editingTrainer.permissions?.commissions || false} disabled={!editingTrainer.is_manager} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, commissions: c}})} />
                </div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
                  <span className="text-grey flex items-center gap-1">Raporlar <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span>
                  <Switch checked={editingTrainer.permissions?.reports || false} disabled={!editingTrainer.is_manager} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, reports: c}})} />
                </div>
              </div>
            </div>

            {/* İşletme Ayarları */}
            <div className={`border rounded-xl p-3 space-y-2 ${!editingTrainer.is_manager ? 'border-amber-200 bg-amber-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-navy flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  {t("common:businessSettings")}
                  <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                </span>
                <Switch
                  checked={editingTrainer.permissions?.settings || false}
                  disabled={!editingTrainer.is_manager}
                  onCheckedChange={(c) => setEditingTrainer({
                    ...editingTrainer, 
                    permissions: {
                      ...editingTrainer.permissions, 
                      settings: c,
                      rooms: c ? editingTrainer.permissions?.rooms : false,
                      packages: c ? editingTrainer.permissions?.packages : false,
                      price_list: c ? editingTrainer.permissions?.price_list : false,
                      trainers: c ? editingTrainer.permissions?.trainers : false,
                      employees: c ? editingTrainer.permissions?.employees : false
                    }
                  })}
                />
              </div>
              <div className={`ml-6 space-y-1.5 ${!editingTrainer.is_manager || editingTrainer.permissions?.settings === false ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t('common:rooms')}</span>
                  <Switch checked={editingTrainer.permissions?.rooms || false} disabled={!editingTrainer.is_manager || editingTrainer.permissions?.settings === false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, rooms: c}})} />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t("packages:packagesLabel")}</span>
                  <Switch checked={editingTrainer.permissions?.packages || false} disabled={!editingTrainer.is_manager || editingTrainer.permissions?.settings === false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, packages: c}})} />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t("common:priceListsLabel")}</span>
                  <Switch checked={editingTrainer.permissions?.price_list || false} disabled={!editingTrainer.is_manager || editingTrainer.permissions?.settings === false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, price_list: c}})} />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t('common:trainers')}</span>
                  <Switch checked={editingTrainer.permissions?.trainers || false} disabled={!editingTrainer.is_manager || editingTrainer.permissions?.settings === false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, trainers: c}})} />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t('employees:employees')}</span>
                  <Switch checked={editingTrainer.permissions?.employees || false} disabled={!editingTrainer.is_manager || editingTrainer.permissions?.settings === false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, employees: c}})} />
                </div>
              </div>
            </div>

            {/* Ayarlar Sayfası - alt yetkilendirme (Yönetici-only) */}
            <div className="border rounded-xl p-3 space-y-2 border-amber-200 bg-amber-50/30" data-testid="settings-page-permissions-block-trainer">
              <div className="flex items-center justify-between">
                <span className="font-medium text-navy flex items-center gap-2">
                  <Layers className="w-4 h-4" /> {t("employees:settingsPagePermissions", "Ayarlar Sayfası")}
                  <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t("employees:manager")}</span>
                </span>
              </div>
              <p className="text-xs text-grey">{t("employees:settingsPagePermissionsHint", "Sol menüdeki Ayarlar sayfasında hangi bölümleri görebileceğini seçin.")}</p>
              <div className={`ml-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5 ${!editingTrainer.is_manager ? "opacity-50" : ""}`}>
                {[
                  { k: "settings_business_info", label: t("settings:businessInfo") },
                  { k: "settings_pricing", label: t("settings:cardPricesTitle") },
                  { k: "settings_accruals", label: t("settings:accrualDaysTitle") },
                  { k: "settings_working_hours", label: t("settings:workingHoursTitle") },
                  { k: "settings_branches", label: t("settings:branchesTitle") },
                  { k: "settings_training_types", label: t("settings:trainingTypesTitle") },
                  { k: "settings_measurements", label: t("settings:measurementParamsTitle") },
                  { k: "settings_expense_types", label: t("settings:expenseTypesTitle") },
                  { k: "settings_numbering", label: t("employees:numberingSettings", "Numaralandırma") },
                  { k: "settings_currency", label: t("common:currency") },
                ].map((it) => (
                  <div key={it.k} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm" data-testid={`perm-${it.k}-trainer`}>
                    <span className="text-grey">{it.label}</span>
                    <Switch
                      checked={editingTrainer.permissions?.[it.k] || false}
                      disabled={!editingTrainer.is_manager}
                      onCheckedChange={(c) => setEditingTrainer({ ...editingTrainer, permissions: { ...editingTrainer.permissions, [it.k]: c } })}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* İletişim */}
            <div className="border rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-navy" />
                  <span className="font-medium text-navy">{t('business:contact')}</span>
                </div>
                <Switch
                  checked={editingTrainer.permissions?.communication !== false}
                  onCheckedChange={(c) => {
                    setEditingTrainer({
                      ...editingTrainer, 
                      permissions: {
                        ...editingTrainer.permissions, 
                        communication: c,
                        messages: c ? editingTrainer.permissions?.messages : false,
                        ratings: c ? editingTrainer.permissions?.ratings : false,
                        surveys: c ? editingTrainer.permissions?.surveys : false,
                        events: c ? editingTrainer.permissions?.events : false,
                        evaluations: c ? editingTrainer.permissions?.evaluations : false,
                        goals: c ? editingTrainer.permissions?.goals : false
                      }
                    });
                  }}
                />
              </div>
              <div className={`space-y-1.5 ml-2 ${!editingTrainer.permissions?.communication && editingTrainer.permissions?.communication !== undefined ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t('common:messages')}</span>
                  <Switch checked={editingTrainer.permissions?.messages !== false} disabled={editingTrainer.permissions?.communication === false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, messages: c}})} />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t('business:evaluations')}</span>
                  <Switch checked={editingTrainer.permissions?.ratings || false} disabled={editingTrainer.permissions?.communication === false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, ratings: c}})} />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t("surveys:surveysLabel")}</span>
                  <Switch checked={editingTrainer.permissions?.surveys || false} disabled={editingTrainer.permissions?.communication === false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, surveys: c}})} />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t("events:eventsLabel")}</span>
                  <Switch checked={editingTrainer.permissions?.events || false} disabled={editingTrainer.permissions?.communication === false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, events: c}})} />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t("evaluations:memberEvaluations")}</span>
                  <Switch checked={editingTrainer.permissions?.evaluations || false} disabled={editingTrainer.permissions?.communication === false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, evaluations: c}})} />
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span className="text-grey">{t("evaluations:goalsAndBadges")}</span>
                  <Switch checked={editingTrainer.permissions?.goals || false} disabled={editingTrainer.permissions?.communication === false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, goals: c}})} />
                </div>
              </div>
            </div>

            {/* Gelir Görme İzni */}
            <div className="border rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-navy" />
                  <span className="font-medium text-navy">{t("reports:viewRevenueInfo")}</span>
                  <span className="text-xs text-blue-600 bg-blue-100 px-1 rounded">{t("common:private")}</span>
                </div>
                <Switch checked={editingTrainer.permissions?.can_view_revenue || false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, can_view_revenue: c}})} />
              </div>
              <p className="text-xs text-grey mt-2">{t("reports:ifPermOpenCanSeeIncome")}</p>
            </div>

            {/* Üyeyi Pasifleştirme İzni */}
            <div className="border rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-navy" />
                  <span className="font-medium text-navy">{t("members:deactivateMemberPermission", "Üyeyi Pasifleştirme")}</span>
                </div>
                <Switch data-testid="trainer-perm-deactivate-members" checked={editingTrainer.permissions?.can_deactivate_members || false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, can_deactivate_members: c}})} />
              </div>
              <p className="text-xs text-grey mt-2">{t("members:deactivateMemberPermissionHint", "Açıksa üyeleri pasif/aktif yapabilir.")}</p>
            </div>

            {/* Devir Paketi Girişi */}
            <div className={`border rounded-xl p-3 ${!editingTrainer.is_manager ? 'opacity-50' : ''}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-navy" />
                  <span className="font-medium text-navy">Devir Paketi Girişi</span>
                  <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                </div>
                <Switch data-testid="trainer-perm-transfer-package" checked={editingTrainer.is_manager && editingTrainer.permissions?.transfer_package_sale !== false} disabled={!editingTrainer.is_manager} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, transfer_package_sale: c}})} />
              </div>
              <p className="text-xs text-grey mt-2">Açıksa sistem öncesi satılmış paketleri (kalan dersleriyle) girebilir.</p>
            </div>

            {/* Manuel Rezervasyon Tamamlama İzni */}
            {editingTrainer.is_manager && (
              <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-amber-600" />
                    <span className="font-medium text-navy">{t("common:manualReservationCompletion")}</span>
                    <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                  </div>
                  <Switch checked={editingTrainer.permissions?.manual_reservation_complete || false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, manual_reservation_complete: c}})} />
                </div>
                <p className="text-xs text-grey mt-2">{t("employees:ifPermOpenCanMarkComplete")}</p>
              </div>
            )}

            {/* Manuel Rezervasyon Onaylama İzni */}
            {editingTrainer.is_manager && (
              <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-amber-600" />
                    <span className="font-medium text-navy">{t("admin:manualReservationApproval")}</span>
                    <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                  </div>
                  <Switch checked={editingTrainer.permissions?.manual_reservation_approve || false} onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, manual_reservation_approve: c}})} />
                </div>
                <p className="text-xs text-grey mt-2">{t("employees:skipApprovalHint")}</p>
              </div>
            )}
            
            {/* Geçmiş Tarihli Rezervasyon İzni */}
            {editingTrainer.is_manager && (
              <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50" data-testid="perm-backdate_reservations-trainer-block">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-amber-600" />
                    <span className="font-medium text-navy">{t("employees:backdateReservations", "Geçmiş Tarihli Rezervasyon")}</span>
                    <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                  </div>
                  <Switch
                    checked={editingTrainer.permissions?.backdate_reservations !== false}
                    onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, backdate_reservations: c}})}
                    data-testid="perm-backdate_reservations-trainer"
                  />
                </div>
                <p className="text-xs text-grey mt-2">Açıksa: Yönetici, "Min. Önceden Rezervasyon" süresine bakmaksızın geçmiş tarihli rezervasyon oluşturabilir.</p>
              </div>
            )}
            
            {/* Ders Açıklaması Düzenleme İzni */}
            {editingTrainer.is_manager && (
              <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50" data-testid="perm-lesson_note_edit-trainer-block">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-amber-600" />
                    <span className="font-medium text-navy">{t("employees:lessonNoteEdit", "Ders Açıklaması Düzenleme")}</span>
                    <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                  </div>
                  <Switch
                    checked={editingTrainer.permissions?.lesson_note_edit !== false}
                    onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, lesson_note_edit: c}})}
                    data-testid="perm-lesson_note_edit-trainer"
                  />
                </div>
                <p className="text-xs text-grey mt-2">Açıksa: Tamamlanan derslere açıklama (not) ekleyebilir ve sonradan düzenleyebilir.</p>
              </div>
            )}
            
            {/* Üye No / Paket No Düzenleme İzinleri */}
            {editingTrainer.is_manager && (
              <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50" data-testid="perm-member-package-no-edit-trainer-block">
                <div className="flex items-center gap-2 mb-3">
                  <Edit className="w-4 h-4 text-amber-600" />
                  <span className="font-medium text-navy">{t("employees:numberEditPermissions", "Numara Düzenleme")}</span>
                  <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="flex items-center justify-between bg-white rounded-lg p-2 border border-slate-200">
                    <span className="text-sm">{t("employees:memberNoEdit", "Üye No Düzenleme")}</span>
                    <Switch
                      checked={editingTrainer.permissions?.member_no_edit !== false}
                      onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, member_no_edit: c}})}
                      data-testid="perm-member_no_edit-trainer"
                    />
                  </label>
                  <label className="flex items-center justify-between bg-white rounded-lg p-2 border border-slate-200">
                    <span className="text-sm">{t("employees:packageNoEdit", "Paket No Düzenleme")}</span>
                    <Switch
                      checked={editingTrainer.permissions?.package_no_edit !== false}
                      onCheckedChange={(c) => setEditingTrainer({...editingTrainer, permissions: {...editingTrainer.permissions, package_no_edit: c}})}
                      data-testid="perm-package_no_edit-trainer"
                    />
                  </label>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" className="rounded-xl" onClick={onClose}>{t('common:cancel')}</Button>
          <Button className="bg-navy hover:bg-navy/90 rounded-xl" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Kaydediliyor..." : t('common:save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
